import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ButtonSpinner } from './ui/Spinner';

const ADD_NEW = '__add_new__';

// Custom Time Picker Component with 15-minute increments
const TimePicker = ({ label, value, onChange, required = false, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTime, setSelectedTime] = useState(value || '');

    // Generate 15-minute time slots
    const timeSlots = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            timeSlots.push(timeString);
        }
    }

    // Update selected time when value prop changes
    useEffect(() => {
        setSelectedTime(value || '');
    }, [value]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.time-picker-container')) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleTimeSelect = (time) => {
        setSelectedTime(time);
        onChange({ target: { value: time } });
        setIsOpen(false);
    };

    const formatDisplayTime = (timeString) => {
        if (!timeString) return '';
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    return (
        <div className={`relative time-picker-container ${className}`}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="block w-full pl-10 pr-3 py-2 border-0 bg-transparent 
                         text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
                         focus:outline-none focus:ring-0 text-sm text-left hover:text-gray-700 dark:hover:text-gray-300
                         transition-colors"
            >
                {selectedTime ? formatDisplayTime(selectedTime) : 'Select time'}
            </button>
            
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {timeSlots.map((time) => (
                        <button
                            key={time}
                            type="button"
                            onClick={() => handleTimeSelect(time)}
                            className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 
                                     transition-colors text-sm
                                     ${selectedTime === time ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                        >
                            {formatDisplayTime(time)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Custom Date Time Input Component
const DateTimeInput = ({ label, value, onChange, required = false, className = "" }) => {
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');

    // Initialize date and time from value prop
    useEffect(() => {
        if (value) {
            const [datePart, timePart] = value.split('T');
            setDate(datePart || '');
            setTime(timePart || '');
        } else {
            // Default to today's date if no value provided
            const today = new Date().toISOString().split('T')[0];
            setDate(today);
            setTime('');
        }
    }, [value]);

    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setDate(newDate);
        if (newDate && time) {
            onChange({ target: { value: `${newDate}T${time}` } });
        } else if (newDate) {
            // If only date is set, still update the value for validation
            onChange({ target: { value: `${newDate}T${time || '00:00'}` } });
        }
    };

    const handleTimeChange = (e) => {
        const newTime = e.target.value;
        setTime(newTime);
        if (date && newTime) {
            onChange({ target: { value: `${date}T${newTime}` } });
        } else if (newTime) {
            // If only time is set, still update the value for validation
            onChange({ target: { value: `${date || new Date().toISOString().split('T')[0]}T${newTime}` } });
        }
    };

    return (
        <div className={`${className}`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-200 focus-within:border-primary-500 dark:focus-within:border-primary-400 focus-within:shadow-md">
                <div className="grid grid-cols-2 gap-3">
                    {/* Date Input */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <input
                            type="date"
                            value={date}
                            onChange={handleDateChange}
                            required={required}
                            className="block w-full pl-10 pr-3 py-2 border-0 bg-transparent 
                                     text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
                                     focus:outline-none focus:ring-0 text-sm"
                            placeholder="mm/dd/yyyy"
                        />
                    </div>
                    
                    {/* Time Input */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                <polyline points="12,6 12,12 16,14" strokeWidth="2"/>
                            </svg>
                        </div>
                        <TimePicker
                            label=""
                            value={time}
                            onChange={handleTimeChange}
                            required={required}
                            className=""
                        />
                    </div>
                </div>
                
                {/* Bottom border indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-primary-600 rounded-b-lg opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-within:opacity-100"></div>
            </div>
        </div>
    );
};

// Reusable Input Component
const Input = ({ label, type = "text", value, onChange, required = false, className = "", ...props }) => (
    <div className={`relative ${className}`}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            className="block w-full rounded-lg border border-beige-200 dark:border-gray-600 
                     bg-beige-50 dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                     focus:border-primary-500 dark:focus:border-primary-400 
                     focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400
                     disabled:opacity-50 disabled:cursor-not-allowed"
            {...props}
        />
    </div>
);

// Reusable Select Component
const Select = ({ label, value, onChange, options = [], loading, required = false, className = "" }) => (
    <div className={`relative ${className}`}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select
            value={value}
            onChange={onChange}
            required={required}
            disabled={loading}
            className="block w-full rounded-lg border border-beige-200 dark:border-gray-600 
                     bg-beige-50 dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                     focus:border-primary-500 dark:focus:border-primary-400 
                     focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <option value="">Select {label}</option>
            {Array.isArray(options) && options.map(option => (
                <option key={option.id} value={option.id}>{option.name}</option>
            ))}
            <option value={ADD_NEW}>+ Add new {label.toLowerCase()}...</option>
        </select>
    </div>
);

const SessionForm = ({ onSuccess, session }) => {
    const [locations, setLocations] = useState([]);
    const [stakes, setStakes] = useState([]);
    const [locationId, setLocationId] = useState(session ? session.location.id : '');
    const [stakeId, setStakeId] = useState(session ? session.stake.id : '');
    const [newLocation, setNewLocation] = useState('');
    const [newStake, setNewStake] = useState('');
    const [loadingLocations, setLoadingLocations] = useState(true);
    const [loadingStakes, setLoadingStakes] = useState(true);
    const [locationCreating, setLocationCreating] = useState(false);
    const [stakeCreating, setStakeCreating] = useState(false);
    const [stakeLevel, setStakeLevel] = useState('');
    const [stakeBigBlind, setStakeBigBlind] = useState('');
    const [stakeTypicalBuyIn, setStakeTypicalBuyIn] = useState('');
    const [startTime, setStartTime] = useState(session ? session.start_time.slice(0, 16) : new Date().toISOString().slice(0, 16));
    const [endTime, setEndTime] = useState(session ? session.end_time.slice(0, 16) : '');
    const [durationHours, setDurationHours] = useState('');
    const [durationMinutes, setDurationMinutes] = useState('');
    const [inputMode, setInputMode] = useState('start_end');
    const [buyIn, setBuyIn] = useState(session ? session.buy_in : '');
    const [cashOut, setCashOut] = useState(session ? session.cash_out : '');
    const [rakeback, setRakeback] = useState(session ? session.rakeback : '');
    const [technicalGrade, setTechnicalGrade] = useState(session ? session.technical_grade : '');
    const [mentalGrade, setMentalGrade] = useState(session ? session.mental_grade : '');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoadingLocations(true);
        axios.get('/api/locations/')
            .then(res => {
                // API now returns unpaginated data
                setLocations(res.data || []);
            })
            .finally(() => setLoadingLocations(false));

        setLoadingStakes(true);
        axios.get('/api/stakes/')
            .then(res => {
                // API now returns unpaginated data
                setStakes(res.data || []);
            })
            .finally(() => setLoadingStakes(false));
    }, []);

    const handleLocationChange = (e) => {
        setLocationId(e.target.value);
        if (e.target.value !== ADD_NEW) setNewLocation('');
    };
    const handleStakeChange = (e) => {
        setStakeId(e.target.value);
        if (e.target.value !== ADD_NEW) setNewStake('');
    };

    const handleCreateLocation = async () => {
        if (!newLocation) return;
        setLocationCreating(true);
        try {
            const res = await axios.post('/api/locations/', { name: newLocation });
            setLocations(locs => [...locs, res.data]);
            setLocationId(res.data.id);
            setNewLocation('');
        } catch {
            setError('Failed to create location.');
        }
        setLocationCreating(false);
    };
    const handleCreateStake = async () => {
        if (!newStake) return;
        setStakeCreating(true);
        try {
            const res = await axios.post('/api/stakes/', {
                name: newStake,
                level: stakeLevel || 0,
                big_blind: stakeBigBlind || null,
                typical_buy_in: stakeTypicalBuyIn || null
            });
            setStakes(sts => [...sts, res.data]);
            setStakeId(res.data.id);
            setNewStake('');
            setStakeLevel('');
            setStakeBigBlind('');
            setStakeTypicalBuyIn('');
        } catch {
            setError('Failed to create stake.');
        }
        setStakeCreating(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);
        let finalEndTime = endTime;
        if (inputMode === 'start_duration') {
            if (!startTime || (!durationHours && !durationMinutes)) {
                setError('Please provide start time and duration.');
                setLoading(false);
                return;
            }
            const start = new Date(startTime);
            const end = new Date(start);
            end.setHours(end.getHours() + Number(durationHours || 0));
            end.setMinutes(end.getMinutes() + Number(durationMinutes || 0));
            finalEndTime = end.toISOString().slice(0, 16);
        }
        try {
            if (session) {
                await axios.patch(`/api/sessions/${session.id}/`, {
                    location_id: locationId,
                    stake_id: stakeId,
                    start_time: startTime,
                    end_time: finalEndTime,
                    buy_in: buyIn,
                    cash_out: cashOut,
                    rakeback: rakeback || 0,
                    technical_grade: technicalGrade || null,
                    mental_grade: mentalGrade || null
                });
                setSuccess('Session updated!');
            } else {
                await axios.post('/api/sessions/', {
                    location_id: locationId,
                    stake_id: stakeId,
                    start_time: startTime,
                    end_time: finalEndTime,
                    buy_in: buyIn,
                    cash_out: cashOut,
                    rakeback: rakeback || 0,
                    technical_grade: technicalGrade || null,
                    mental_grade: mentalGrade || null
                });
                setSuccess('Session logged!');
                setLocationId('');
                setStakeId('');
                setStartTime(new Date().toISOString().slice(0, 16));
                setEndTime('');
                setDurationHours('');
                setDurationMinutes('');
                setBuyIn('');
                setCashOut('');
                setRakeback('');
            }
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(session ? 'Failed to update session.' : 'Failed to log session.');
        }
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <Select
                label="Location"
                value={locationId}
                onChange={handleLocationChange}
                options={locations || []}  // Ensure options is always an array
                loading={loadingLocations}
                required
            />

            {locationId === ADD_NEW && (
                <div className="flex gap-2 items-center">
                    <Input
                        label="New Location"
                        value={newLocation}
                        onChange={(e) => setNewLocation(e.target.value)}
                        className="flex-1"
                    />
                    <button
                        type="button"
                        onClick={handleCreateLocation}
                        disabled={locationCreating || !newLocation}
                        className="h-10 px-4 mt-6 rounded-lg bg-primary-600 text-white
                                 hover:bg-primary-700 focus:outline-none focus:ring-2
                                 focus:ring-primary-500 focus:ring-offset-2
                                 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {locationCreating ? <ButtonSpinner /> : 'Add'}
                    </button>
                </div>
            )}

            <Select
                label="Stake"
                value={stakeId}
                onChange={handleStakeChange}
                options={stakes || []}  // Ensure options is always an array
                loading={loadingStakes}
                required
            />

            {stakeId === ADD_NEW && (
                <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                        <Input
                            label="New Stake (e.g. 2/5)"
                            value={newStake}
                            onChange={(e) => setNewStake(e.target.value)}
                            className="flex-1"
                        />
                        <Input
                            label="Level"
                            type="number"
                            value={stakeLevel}
                            onChange={(e) => setStakeLevel(e.target.value)}
                            className="w-24"
                        />
                    </div>
                    <div className="flex gap-2 items-center">
                        <Input
                            label="Big Blind ($)"
                            type="number"
                            value={stakeBigBlind}
                            onChange={(e) => setStakeBigBlind(e.target.value)}
                            placeholder="e.g. 5"
                            className="w-28"
                        />
                        <Input
                            label="Typical Buy-in ($)"
                            type="number"
                            value={stakeTypicalBuyIn}
                            onChange={(e) => setStakeTypicalBuyIn(e.target.value)}
                            placeholder="e.g. 1000"
                            className="flex-1"
                        />
                        <button
                            type="button"
                            onClick={handleCreateStake}
                            disabled={stakeCreating || !newStake}
                            className="h-10 px-4 mt-6 rounded-lg bg-primary-600 text-white
                                     hover:bg-primary-700 focus:outline-none focus:ring-2
                                     focus:ring-primary-500 focus:ring-offset-2
                                     disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {stakeCreating ? <ButtonSpinner /> : 'Add'}
                        </button>
                    </div>
                </div>
            )}

            <RadioGroup value={inputMode} onChange={setInputMode} className="mb-6">
                <div className="flex gap-4">
                    <RadioGroup.Option value="start_end" className={({ checked }) => `
                        relative flex items-center px-4 py-3 cursor-pointer rounded-lg border transition-all duration-200
                        ${checked ? 'bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-700 shadow-sm' : 
                                  'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}
                    `}>
                        {({ checked }) => (
                            <>
                                <div className={`w-4 h-4 rounded-full border-2 mr-3 transition-colors
                                    ${checked ? 'border-primary-600 dark:border-primary-400' : 
                                              'border-gray-400 dark:border-gray-600'}
                                `}>
                                    {checked && (
                                        <div className="w-2 h-2 rounded-full bg-primary-600 
                                                      dark:bg-primary-400 m-0.5" />
                                    )}
                                </div>
                                <span className="text-gray-900 dark:text-white font-medium">Start & End Time</span>
                            </>
                        )}
                    </RadioGroup.Option>
                    <RadioGroup.Option value="start_duration" className={({ checked }) => `
                        relative flex items-center px-4 py-3 cursor-pointer rounded-lg border transition-all duration-200
                        ${checked ? 'bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-700 shadow-sm' : 
                                  'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}
                    `}>
                        {({ checked }) => (
                            <>
                                <div className={`w-4 h-4 rounded-full border-2 mr-3 transition-colors
                                    ${checked ? 'border-primary-600 dark:border-primary-400' : 
                                              'border-gray-400 dark:border-gray-600'}
                                `}>
                                    {checked && (
                                        <div className="w-2 h-2 rounded-full bg-primary-600 
                                                      dark:bg-primary-400 m-0.5" />
                                    )}
                                </div>
                                <span className="text-gray-900 dark:text-white font-medium">Start + Duration</span>
                            </>
                        )}
                    </RadioGroup.Option>
                </div>
            </RadioGroup>

            <div className="space-y-4">
                <DateTimeInput
                    label="Start Time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                />
                {inputMode === 'start_end' ? (
                    <DateTimeInput
                        label="End Time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        required
                    />
                ) : (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Duration
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Hours"
                                type="number"
                                value={durationHours}
                                onChange={(e) => setDurationHours(e.target.value)}
                                min="0"
                                className=""
                            />
                            <Input
                                label="Minutes"
                                type="number"
                                value={durationMinutes}
                                onChange={(e) => setDurationMinutes(e.target.value)}
                                min="0"
                                max="59"
                                step="15"
                                className=""
                            />
                        </div>
                    </div>
                )}
            </div>

            <Input
                label="Total In (Buy-In)"
                type="number"
                value={buyIn}
                onChange={(e) => setBuyIn(e.target.value)}
                required
                min="0"
                step="0.01"
            />

            <Input
                label="Total Out (Cash-Out)"
                type="number"
                value={cashOut}
                onChange={(e) => setCashOut(e.target.value)}
                required
                min="0"
                step="0.01"
            />

            <Input
                label="Rakeback"
                type="number"
                value={rakeback}
                onChange={(e) => setRakeback(e.target.value)}
                min="0"
                step="0.01"
            />

            {/* Session Performance Grades */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Technical Grade (Optional)
                    </label>
                    <div className="flex gap-2">
                        {['A', 'B', 'C'].map(grade => (
                            <button
                                key={grade}
                                type="button"
                                onClick={() => setTechnicalGrade(technicalGrade === grade ? '' : grade)}
                                className={`flex-1 px-3 py-2 rounded-lg border font-semibold transition-colors ${
                                    technicalGrade === grade
                                        ? 'bg-blue-100 dark:bg-blue-900 border-blue-500 dark:border-blue-400 text-blue-700 dark:text-blue-300'
                                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-600'
                                }`}
                            >
                                {grade}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mental Grade (Optional)
                    </label>
                    <div className="flex gap-2">
                        {['A', 'B', 'C'].map(grade => (
                            <button
                                key={grade}
                                type="button"
                                onClick={() => setMentalGrade(mentalGrade === grade ? '' : grade)}
                                className={`flex-1 px-3 py-2 rounded-lg border font-semibold transition-colors ${
                                    mentalGrade === grade
                                        ? 'bg-purple-100 dark:bg-purple-900 border-purple-500 dark:border-purple-400 text-purple-700 dark:text-purple-300'
                                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-purple-300 dark:hover:border-purple-600'
                                }`}
                            >
                                {grade}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <button
                type="submit"
                disabled={loading}
                className="w-full mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg
                         hover:bg-primary-700 focus:outline-none focus:ring-2
                         focus:ring-primary-500 focus:ring-offset-2
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {loading ? <ButtonSpinner /> : (session ? 'Save Changes' : 'Log Session')}
            </button>

            {error && (
                <div className="mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 
                              text-red-700 dark:text-red-400 flex items-center gap-2">
                    <XCircleIcon className="h-5 w-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="mt-4 p-4 rounded-lg bg-green-50 dark:bg-green-900/30 
                              text-green-700 dark:text-green-400 flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                    <span>{success}</span>
                </div>
            )}
        </form>
    );
};

export default SessionForm; 